import { onInvalid } from "@/features/leads/contexts/FormLeadContext";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { refetchCurrentChild } from "@/features/leads/api/useCurrentChild";
import { refetchChilds } from "@/features/leads/api/useChilds";
import { refreshAfterDealCreate } from "@/module/utils/refreshAfterDealCreate";
import useVariants from "@/features/videoProduction/api/useVariants";
import useCurrentVariant from "@/features/videoProduction/api/useCurrentVariant";
import useCurrentIndustry from "@/hooks/globalapis/useCurrentIndustry";
import { useSyncronizer } from "@/hooks/useSyncronizer";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import { CustomInputScopeSchema } from "@/types/general";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  Control,
  UseFormRegister,
  FieldErrors,
  useForm,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import z from "zod";
import { setStartDeal } from "@/features/videoProduction/stores/videoProductionFunctionalSlice";
import { childEndpoints } from "@/config/endpoints";
import axiosInstance from "@/lib/axios";
import { toast } from "sonner";

const DealCreationSchema = z.object({
  name: z.string().min(1, "Deal name is required"),
  count: z.number().min(1, "Count must be at least 1"),
  stageId: z.number().optional(),
  statusId: z.number().optional(),
  subStatusId: z.number().optional(),
  customInputScopes: z.array(CustomInputScopeSchema).optional(),
});

type DealFormData = z.infer<typeof DealCreationSchema>;

/* ---------------- TYPES ---------------- */

type FormVideoProductionContextType = {
  lead: any;
  refetchLead: any;
  variants: any[];
  register: UseFormRegister<DealFormData>;
  control: Control<DealFormData>;
  errors: FieldErrors<DealFormData>;
  handleSubmit: UseFormHandleSubmit<DealFormData>;
  reset: (values?: Partial<DealFormData>) => void;
  watch: UseFormWatch<DealFormData>;
  getValues: UseFormGetValues<DealFormData>;
  setValue: UseFormSetValue<DealFormData>;
  onSubmit: any;
  variant: any;
  isCreating: boolean;
  moduleScopes: { nicheModelName: string; nicheItemId: number }[];
};

/* ---------------- CONTEXT ---------------- */

const FormVideoProductionContext =
  createContext<FormVideoProductionContextType | null>(null);

/* ---------------- PROVIDER ---------------- */

export function VideoProductionProvider({ children }: { children: ReactNode }) {
  const { lead, refetchLead, handleChildChange } = useActiveDataMutation();
  const { variants } = useVariants();
  const { industry } = useCurrentIndustry();
  const dispatch = useAppDispatch();
  const [isCreating, setIsCreating] = useState(false);

  const { variantDetailId } = useAppSelector(
    (state) => state.videoProductionFunctional
  );
  const { variant, refetch } = useCurrentVariant(variantDetailId);

  useSyncronizer(variantDetailId, () => refetch());

  const moduleScopes = useMemo(
    () =>
      variant?.id
        ? [
            {
              nicheModelName: "videoServiceVarients" as const,
              nicheItemId: variant.id,
            },
          ]
        : [],
    [variant?.id]
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<DealFormData>({
    resolver: zodResolver(DealCreationSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      count: 1,
      customInputScopes: [],
    },
  });

  const onSubmit = useCallback(async (data: any) => {
    const targetVariant = variant;
    if (!targetVariant?.id || !lead?.id) return;

    const count = data.count ?? 1;
    const baseName = data.name;

    if (!data.stageId) delete data.stageId;
    if (!data.statusId) delete data.statusId;
    if (!data.subStatusId) delete data.subStatusId;

    setIsCreating(true);
    let lastChildId: number | null = null;
    let successCount = 0;

    try {
      for (let i = 0; i < count; i++) {
        const suffix = count > 1 ? ` #${i + 1}` : "";
        const payload: any = {
          name: `${baseName}${suffix}`,
          leadId: lead.id,
          moduleScopes: [
            {
              nicheModelName: "videoServiceVarients",
              nicheItemId: targetVariant.id,
            },
          ],
          ...(data.stageId && { stageId: data.stageId }),
          ...(data.statusId && { statusId: data.statusId }),
          ...(data.subStatusId && { subStatusId: data.subStatusId }),
          ...(data.customInputScopes?.length && {
            customInputScopes: data.customInputScopes,
          }),
        };

        const res = await axiosInstance.post(childEndpoints, payload);
        const childId = res?.data?.child?.id;
        if (childId) lastChildId = childId;
        successCount++;
      }

      toast.success(
        `${successCount} deal${successCount > 1 ? "s" : ""} created`
      );
      refetchCurrentChild();
      refetchChilds();
      if (lastChildId) handleChildChange(lastChildId);
      dispatch(setStartDeal(false));
      refetchLead();
      refreshAfterDealCreate(lead.id);
      reset();
    } catch (err: any) {
      if (successCount > 0) {
        toast.warning(`${successCount} of ${count} deals created before error`);
        refetchCurrentChild();
        refetchChilds();
        if (lastChildId) handleChildChange(lastChildId);
        refetchLead();
        refreshAfterDealCreate(lead.id);
      } else {
        toast.error(err?.response?.data?.message || "Failed to create deals");
      }
    } finally {
      setIsCreating(false);
    }
  }, [variant, lead, handleChildChange, dispatch, refetchLead, reset]);

  const wrappedHandleSubmit = useCallback<UseFormHandleSubmit<any>>(
    (fn) => handleSubmit(fn, onInvalid),
    [handleSubmit],
  );

  const variantsList = useMemo(() => variants ?? [], [variants]);

  const value = useMemo<FormVideoProductionContextType>(
    () => ({
      lead,
      variants: variantsList,
      refetchLead,
      register,
      control,
      handleSubmit: wrappedHandleSubmit,
      onSubmit,
      reset,
      watch,
      errors,
      getValues,
      setValue,
      variant,
      isCreating,
      moduleScopes,
    }),
    [
      lead,
      variantsList,
      refetchLead,
      register,
      control,
      wrappedHandleSubmit,
      onSubmit,
      reset,
      watch,
      errors,
      getValues,
      setValue,
      variant,
      isCreating,
      moduleScopes,
    ],
  );

  if (industry?.id !== 4) return null;

  return (
    <FormVideoProductionContext.Provider value={value}>
      {children}
    </FormVideoProductionContext.Provider>
  );
}

/* ---------------- HOOK ---------------- */

export function useVideoProduction(): FormVideoProductionContextType {
  const context = useContext(FormVideoProductionContext);

  if (!context) {
    throw new Error(
      "useVideoProduction must be used within VideoProductionProvider"
    );
  }

  return context;
}
